import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";

export default function Register() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register({ name, company, email, password });
      nav("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white grid-bg px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6B00] brand-glow">
            <Sparkle weight="fill" size={18} className="text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-bold">myGenie</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Partners</div>
          </div>
        </Link>

        <h2 className="font-display text-3xl font-bold text-[#0F172A]">Create your partner workspace</h2>
        <p className="mt-2 text-sm text-slate-500">You'll be the Owner — only Owners can create OAuth applications.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" testId="register-name-input">
              <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
            </Field>
            <Field label="Company">
              <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="register-company-input"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
            </Field>
          </div>
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>
          <Field label="Password (min 6 chars)">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password-input"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>

          {error && (
            <div data-testid="register-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          <button type="submit" disabled={loading} data-testid="register-submit-button"
            className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60">
            {loading ? "Creating…" : (<>Create workspace <ArrowRight size={14} weight="bold" /></>)}
          </button>
        </form>

        <div className="mt-6 text-sm text-slate-500">
          Already a partner?{" "}
          <Link to="/login" className="font-medium text-[#FF6B00] hover:underline" data-testid="goto-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
